import React, { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  MdSupervisedUserCircle,
  MdAccountCircle,
  MdPlace,
  MdErrorOutline,
  MdCheckCircle,
  MdCancel,
} from "react-icons/md";
import { BsCalendarPlus } from "react-icons/bs";

import Lead from "../../shared/Lead";
import * as API from "../../api";
import Loading from "../../shared/Loading";
import Select from "../../shared/Select";
import TextArea from "../../shared/TextArea";
import Patient from "../../shared/Patient";
import Checkbox from "../../shared/Checkbox";
import SelectAssignee from "../forms/SelectAssignee";
import SelectPcp from "../forms/SelectPcp";
import SelectDate from "../forms/SelectDate";
import SelectVisitType from "../forms/SelectVisitType";
import FieldError from "../../shared/FieldError";
import { Settings } from "../../helpers/Contexts";

const TaskSchema = Yup.object().shape({
  reason_for_visit: Yup.string().nullable(),
  pcp_id: Yup.string().nullable(),
  pcp: Yup.string().nullable(),
  notes: Yup.string().nullable(),
  patient_availability_notes: Yup.string().nullable(),
  preferred_contact_method: Yup.string().required("Patient preferred contact method is required"),
  request_date: Yup.string().required(
    "Date range is required. Provide date range in the field above or select a range in the calendar"
  ),
  assignee: Yup.string().required(
    "Assignee is required. Assign this task to either a suppport group or agent."
  ),

  procedure_type: Yup.string().required("Visit type is required"),
  date_type: Yup.string().required(
    "Choose date type for this request. Then, select a date in the calendar if patient provides a specific date. For a range, enter range in the calendar or in the field below."
  ),
});
const AppointmentRequestForm = ({ state, dispatch, isCall, formHandler }) => {
  const settings = useContext(Settings);

  const [error, setError] = useState("");
  const [taskSubmitted, setTaskSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  return (
    <Formik
      initialValues={{
        task_type: "appointment_request",
        priority: "high",
        date_type: "",
        patient_id: state.create.patient.id,
        reason_for_visit: "",
        date: "",
        request_date_start: "",
        request_date_end: "",
        assignee: "",
        patient_availability_notes: "",
        procedure_type: "",
        procedure_type_id: "",
        preferred_contact_method: "",
        pcp_id: "",
        pcp_practice_name: "",
        pcp_practice_id: "",
        pcp_practice_address: "",
        pcp: "",
        established: "",
        notes: "",
        patient_booking_availability: true,
        appointment_request_status: "appointmet_request_created",
        assigner_id: settings.me.id,
        assignee_id: "",
        assignee_support_group_id: "",
        assigner_support_group_id: settings.me.support_group_id,
      }}
      validateOnChange={true}
      validationSchema={TaskSchema}
      onSubmit={(values) => {
        const task = {
          task_type: values.task_type,
          priority: "high",
          patient_id: state.create.patient.id,
          established:
            values.established == "new" ? false : values.established == "established" ? true : null,
          pcp_id: values.pcp_id ? values.pcp_id : null,
          pcp_practice_id: values.pcp_practice_id,
          notes: values.notes,
          reason_for_visit: values.reason_for_visit,
          request_date_start: values.request_date_start,
          request_date_end: values.request_date_end,
          patient_availability_notes: values.patient_availability_notes,
          procedure_type_id: values.procedure_type_id,
          preferred_contact_method: values.preferred_contact_method,
          patient_booking_availability: values.patient_booking_availability,
          assignee_id: values.assignee_id && values.assignee_id,
          assignee_support_group_id:
            values.assignee_support_group_id && values.assignee_support_group_id,
          status: "open",
          appointment_request_status: "appointment_request_created",
          assigner_id: values.assigner_id,
          assigner_support_group_id: values.assigner_support_group_id,
        };

        if (isCall) {
          formHandler({
            task: {
              id: state.currentTask.id,
              ...task,
            },
          });
        } else {
          API.postTask({
            task: {
              ...task,
            },
          })
            .then((res) => {
              setLoading(false);
              setTaskSubmitted(true);
              dispatch({ type: "set_loading", payload: true });
              history.push(`/physicians/tasks/item/${res.task.id}`);
            })
            .catch((err) => {
              console.log(err.data);
              setError(err);
            });
        }
      }}
    >
      {({ values, isSubmitting, touched, setFieldValue }) => {
        return (
          <Row className="flex">
            <div className="col-md-5 form pad20">
              <Lead message={isCall ? "Appointment Request" : "New Appointment Request"} />
              <br />
              <br />
              <Form>
                <SelectAssignee name="assignee" setFieldValue={setFieldValue} />
                <SelectVisitType
                  name="procedure_type"
                  value={values.procedure_type_id}
                  setFieldValue={setFieldValue}
                />
                <Row>
                  <Col md={6} id="field-left">
                    <label>{"Requested Provider"}</label>
                    {values.procedure_type == "Lab/Diagnostic Only" ? (
                      <select disabled className="dropdown-select">
                        <option value={""}>{"N/A for Lab/Diagnostic Only"}</option>
                      </select>
                    ) : (
                      <SelectPcp name="pcp" active={false} required={false} setFieldValue={setFieldValue} />
                    )}
                  </Col>

                  <Col md={6} id="field-right">
                    <Select label="Patient Status" name="established">
                      {!values.established && (
                        <option value="">{"Select patient status with PCP Group"}</option>
                      )}
                      <option value={"new"}>{"New to PCP Group"}</option>
                      <option value={"established"}>{"Established to PCP Group"}</option>
                    </Select>
                    {!values.established && touched.established && (
                      <FieldError error={"Patient status is required."} />
                    )}
                  </Col>
                </Row>
                <TextArea
                  label="Reason For Visit"
                  name="reason_for_visit"
                  placeholder="Please write a brief summary of Patient's reason for visit if any"
                />
                <SelectDate
                  name="date"
                  setFieldValue={setFieldValue}
                  values={values}
                  isNew={true}
                />{" "}
                {values.date_type == "specific" && !values.request_date_start && (
                  <FieldError error={"Provide a date"} />
                )}
                <TextArea
                  label=""
                  name={"patient_availability_notes"}
                  onChange={(e) => {
                    setFieldValue("request_date", e.target.value) &&
                      setFieldValue("patient_availability_notes", e.target.value);
                  }}
                  placeholder="Include any additional time or date range details here"
                />{" "}
                {values.date_type == "range" &&
                  !values.patient_availability_notes &&
                  !values.request_date_start &&
                  !values.request_date_end && (
                    <>
                      <span id="error">
                        <MdErrorOutline className="error-icon" />
                        Date range is required. Provide date range in the field above or select a
                        range in the calendar
                      </span>
                    </>
                  )}
                <Select label="Patient Preferred Contact Method" name="preferred_contact_method">
                  <option>{"Select patient preferred contact method"}</option>
                  <option value="text">{"Text"}</option>
                  <option value="phone">{"Phone"}</option>
                </Select>
                <Checkbox
                  name="patient_booking_availability"
                  children={
                    <>
                      Patient is available for booking within next 30 minutes
                      <br />
                      <small id="from">
                        If patient is unavailable, please provide availability details in the notes
                        section
                      </small>
                    </>
                  }
                />
                <TextArea label="Notes" name="notes" placeholder="Write additional notes here" />
                <button id="btn outline" type="submit" disabled={isSubmitting}>
                  Submit Appointment Request
                </button>
              </Form>
            </div>

            <div className={"col-md-7 pad20 border-container"}>
              {loading && !taskSubmitted && <Loading message={"Creating Task "} />}
              {error && <Lead message={"Error processing request"} />}

              {values.task_type && (
                <>
                  {" "}
                  <Patient patient={{ ...state.patient }} />
                  <br />
                  <label className="agent">
                    {"Assignee "}{" "}
                    <span id="from">
                      {" "}
                      {values.assignee_id && values.assignee_support_group_id && (
                        <>
                          <MdAccountCircle className="task-agent" />
                          <b> {values.assignee}</b>
                        </>
                      )}
                      {!values.assignee_id && values.assignee_support_group_id && (
                        <>
                          {" "}
                          <MdSupervisedUserCircle className="task-agent" />
                          <b>{values.assignee} </b>
                        </>
                      )}
                    </span>
                  </label>
                  <hr />
                </>
              )}
            </div>
          </Row>
        );
      }}
    </Formik>
  );
};

export default AppointmentRequestForm;
